/**
 * Статистик — `/stats`.
 *
 * Тоонууд өмнө нь хуудас бүрийн дээд талд «Статистик ▼» товчны цаана
 * тарж байсан: ажилтан аль хуудсанд юу байдгийг санахаас өөр аргагүй,
 * хоорондоо харьцуулах ч боломжгүй. Одоо БҮГД энд, талбар тус бүрээр.
 *
 * ⚠️ Энэ хуудас юу ч ТООЦООЛОХГҮЙ — бүх тоо серверийн тайлангийн
 *    endpoint-оос ирнэ (`/reports/*`). Клиент талд нийлбэр бодвол
 *    хуудаслалт, эрхийн шүүлтээс болж дашбоардтай зөрнө.
 *
 * Графикууд `components/stats/`-д (chart сангүй, токен өнгөтэй) —
 * ирсэн утгыг л зурна; хувь, нийлбэр нь зөвхөн ижил хариуны мөрүүдээс.
 *
 * ⚠️ Өртөгтэй холбоотой бүх дүн `COST_ROLES`-оор хаагдана — сервер ч
 *    мөн адил `null` буцаадаг тул энд «•••» нь зөвхөн эмх цэгц.
 *
 * ТАБ (2026-10-05 цэгцлэв): Тойм · Нөөц ба байршил · Нийлүүлэлт.
 *
 * ҮҮРЭГ (2026-09-29 цэгцлэв): нөөц, нийлүүлэлт, салбар, ажилтны тоо —
 * мөнгөгүйгээр ч уншигддаг (цуглуулагч ч нээдэг). Борлуулалт «Тайлан»-д,
 * өдөр бүрийнх «Хуанли»-д, мөнгөний урсгал «Санхүү»-д. Нэг таб дотор ижил
 * тоог карт, тууз, хүснэгт гурвуулаа давтахгүй — нэг л удаа.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatDate, formatMnt, formatMntShort, formatQty, formatRub, formatSize } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { COST_ROLES, type Dashboard, type StockLocations, type StockScope } from '../lib/types';
import { ErrorNote, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { Section } from '../components/section';
import { StatCard, StatGrid } from '../components/stat-card';
import { ColumnChart } from '../components/stats/column-chart';
import { RankBars } from '../components/stats/rank-bars';
import { StatusTiles } from '../components/stats/status-tiles';
import { LocationTree } from '../components/stats/location-tree';
import { Help } from '../components/info-tip';

/*
  ⚠️ «Ажилтан» табыг ХАСАВ (2026-10-05, захиалагчийн цэгцлэл №6, №14):
     дөрвөн тоо л байв (нийт, Оросод, салбарт, агуулахад) — шийдвэрт
     нөлөөлөхгүй. Ажилтны бүртгэл «Тохиргоо → Ажилтан»-д, борлуулалт
     хийсэн ажилтан тайланд хэвээр. `/reports/staff` endpoint ХЭВЭЭР —
     салбарын маягт ажилтан сонгоход хэрэглэдэг.
*/
type Tab = 'overview' | 'stock' | 'supply';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Тойм' },
  { key: 'stock', label: 'Нөөц ба байршил' },
  { key: 'supply', label: 'Нийлүүлэлт' },
];

export function StatsPage() {
  const [tab, setTab] = useState<Tab>('overview');

  return (
    <div className="flex flex-col gap-3">
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'overview' ? <OverviewTab /> : null}
      {tab === 'stock' ? <StockTab /> : null}
      {tab === 'supply' ? <SupplyTab /> : null}
    </div>
  );
}

/** Хүсэлтийн гурван төлвийг нэг дор — таб бүрд давтахгүй. */
function Load<T>({
  q, children,
}: {
  q: { isPending: boolean; error: unknown; data?: T };
  children: (data: T) => React.ReactNode;
}) {
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;
  return <>{q.data ? children(q.data) : null}</>;
}

// ------------------------------------------------------------ Тойм
function OverviewTab() {
  const q = useQuery({
    queryKey: ['dashboard', 'month'],
    queryFn: () => api<Envelope<Dashboard>>('/reports/dashboard?period=month'),
  });

  return (
    <Load q={q}>
      {(res) => {
        const d = res.data;
        /*
          ⚠️ «Одоо байгаа» = `nav.items` (цуглуулсан, бэлэн, түр захиалсан,
             буцаагдсан) — сайдбарын «Бүх гутал»-ын тоотой ИЖИЛ. Өмнө нь
             «Нийт гутал» гэж зарагдсан, актлагдсаныг ч оруулсан тоо
             харуулж, сайдбараас зөрдөг байв; хувь нь ч борлуулалт өсөх
             тусам буурдаг байв. «Нөөцөд» гэж нэрлэхгүй — «Бүх гутал»-ын
             «Нөөцөд» шүүлтүүр нь зөвхөн бэлэн + буцаагдсан (жижиг олонлог).
        */
        const onHand = d.nav.items;
        // Аялал ба эхний үлдэгдэл ТУСДАА (№2) — графикт зөвхөн аялал
        const tripThisYear = d.intake.series.reduce((n, p) => n + p.trip, 0);
        // Сервер сарыг «1»…«12» гэж өгнө — энэ сарын баганыг онцолно
        const thisMonth = d.intake.series.findIndex((p) => p.label === String(new Date().getMonth() + 1));
        const sellablePct = onHand > 0 ? (d.counts.sellable / onHand) * 100 : 0;

        return (
          <div className="flex flex-col gap-3">
            <StatGrid>
              <StatCard
                label="Одоо байгаа"
                icon="box"
                value={formatQty(onHand)}
                delta={{ text: `нийт бүртгэгдсэн ${formatQty(d.counts.total)}` }}
                info={
                  <Help
                    about={
                      <>
                        Цуглуулсан, бэлэн, түр захиалсан, буцаагдсан гутал —
                        сайдбарын «Бүх гутал»-ын тоо. Зарагдсан, гэмтэлтэй,
                        актлагдсан орохгүй. «Нийт бүртгэгдсэн»-д бүгд орно.
                      </>
                    }
                    howTo="Төлөв тус бүрийн задаргааг доорх «Төлөв бүрээр» картуудаас харна."
                  />
                }
              />
              <StatCard
                label="Зарагдахад бэлэн"
                icon="store"
                value={formatQty(d.counts.sellable)}
                delta={{ text: `байгаагийн ${Math.round(sellablePct)}%` }}
                meter={sellablePct}
                info={
                  <Help
                    about="Зарагдах байршилд (лангуу, үзмэр) байгаа бэлэн эсвэл буцаагдсан гутал — кассаар шууд зарж болно."
                    howTo="Хувь бага бол агуулахад хүлээгдэж буй гутал олон байна — агуулахаас салбар руу шилжүүлэг хийнэ."
                  />
                }
              />
              <StatCard
                label="Шошгогүй"
                icon="box"
                value={formatQty(d.counts.unlabeled)}
                delta={{ text: 'серийн дугаар хүлээж буй', good: d.counts.unlabeled === 0 }}
                info={
                  <Help
                    about="Баркод (серийн дугаар) олгогдоогүй гутал. Зарагдсан, актлагдсан орохгүй."
                    howTo="«Бүх гутал» → «Шошгогүй» шүүлтүүрээр олоод агуулахад баркод олгож шошго наана."
                  />
                }
              />
              <StatCard
                label="0₮ үнэтэй"
                icon="coins"
                value={formatQty(d.counts.zeroPriced)}
                delta={{ text: 'зарагдах байршилд', good: d.counts.zeroPriced === 0 }}
                info={
                  <Help
                    about="Лангуу, үзмэрт байгаа ч үнэ тавигдаагүй гутал. Касс ийм гутлыг зарахгүй."
                    howTo="«Бүх гутал» → «Үнэгүй» шүүлтүүрээр олж үнэ тавина."
                  />
                }
              />
            </StatGrid>

            <StatGrid>
              {d.value ? (
                <>
                  <StatCard
                    label="Нөөцийн өртөг"
                    icon="coins"
                    value={formatMntShort(d.value.landedCostMnt)}
                    delta={{ text: 'бүх байршил · тээвэр, гааль оруулсан' }}
                    info={
                      <Help about="Одоо байгаа гутлын авсан өртгийн нийлбэр — тээвэр, гааль хуваарилсны дараах. Өртөг хуваарилаагүй аяллын гутал 0-ээр орно." />
                    }
                  />
                  <StatCard
                    label="Жижиглэнгийн үнэ"
                    icon="chart"
                    value={formatMntShort(d.value.retailMnt)}
                    delta={{ text: `${formatQty(d.value.onHandCount)} гутлын нийлбэр` }}
                    info={
                      <Help about="Одоо байгаа гутлын зарах үнийн нийлбэр — бүгд хөнгөлөлтгүй зарагдвал орох орлого." />
                    }
                  />
                  <StatCard
                    label="Дундаж үнэ"
                    icon="receipt"
                    value={formatMnt(d.value.avgPriceMnt)}
                    delta={{ text: 'нэг гутлаар' }}
                    info={
                      <Help about="Бэлэн, буцаагдсан төлөвтэй, үнэ тавьсан гутлын дундаж зарах үнэ. 0₮ гутал орохгүй." />
                    }
                  />
                </>
              ) : null}
              {/* Өртөг биш — цуглуулагч ч харах ёстой, тиймээс өртгийн нөхцөлөөс гадуур */}
              <StatCard
                label="Sync хийгээгүй"
                icon="truck"
                value={formatQty(d.counts.unsynced)}
                delta={{ text: 'Оросоос ирээгүй', good: d.counts.unsynced === 0 }}
              />
            </StatGrid>

            {/*
              ⚠️ Тайлангийн «Сарын нэгтгэл»-ийн «Цуглуулсан» баганатай ИЖИЛ
                 цуврал — гэхдээ Тайлан COST_ROLES-д л нээлттэй, цуглуулагч
                 (dashboard.view) энд л хардаг тул хоёулаа үлдэнэ. Нэр нь
                 Хуанлийн «Шинээр бүртгэгдсэн» (үүссэн өдөр)-өөс ялгаатай:
                 энэ нь цуглуулсан (шошгоны) огноогоор.
            */}
            {/*
              ⚠️ Эхний үлдэгдэл (системээс өмнө байсан гутал) шошгоны огноогоороо
                 тоологдож, тэр сард огцом өндөр багана гаргадаг байв — бодит
                 сарын орлогыг нууж байсан (захиалагчийн цэгцлэл №2). Одоо хоёр
                 тусдаа үзүүлэлт, графикт зөвхөн аялал.
            */}
            <Section
              title="Гутал хаанаас орсон"
              subtitle={`энэ онд аяллаар ${formatQty(tripThisYear)} ш`}
              note="график — аяллын цуглуулсан огноогоор, эхний үлдэгдэл орохгүй"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <StatCard
                  label="Аяллаар орсон"
                  icon="truck"
                  value={formatQty(d.sources.trip.total)}
                  delta={{ text: `одоо ${formatQty(d.sources.trip.onHand)} нь байгаа` }}
                  to="/items?origin=trip&status=all"
                  info={
                    <Help
                      about="Оросын аяллаар цуглуулж, агуулахад бүртгэсэн бүх гутал — бүх хугацаанд. «Одоо байгаа» нь зарагдаагүй, актлагдаагүй хэсэг."
                      howTo="Сар бүрийн орлогыг доорх графикаас, аялал тус бүрийг «Аялал - Орос»-оос харна."
                    />
                  }
                />
                <StatCard
                  label="Эхний үлдэгдэл"
                  icon="box"
                  value={formatQty(d.sources.opening.total)}
                  delta={{ text: `одоо ${formatQty(d.sources.opening.onHand)} нь байгаа` }}
                  to="/items?origin=opening&status=all"
                  info={
                    <Help
                      about="Систем ашиглаж эхлэхээс өмнө байсан, шошготой гутал — нэг удаа бүртгэсэн. Шинэ орлого биш тул сарын графикт орохгүй."
                      howTo="Карт дээр дарж эхний үлдэгдлийн гутлыг жагсаалтаар харна."
                    />
                  }
                />
              </div>
              <ColumnChart
                ariaLabel="Сар бүр аяллаар орсон гутал"
                highlight={thisMonth >= 0 ? thisMonth : undefined}
                points={d.intake.series.map((p) => ({
                  key: p.label,
                  label: p.label,
                  title: /^\d+$/.test(p.label) ? `${p.label}-р сар` : p.label,
                  value: p.trip,
                }))}
              />
            </Section>

            {/* Байршил бүрийн үлдэгдэл → «Нөөц ба байршил» таб (№4, 2026-10-05) */}
            <Section
              title="Төлөв бүрээр"
              subtitle={`нийт ${formatQty(d.counts.total)} гутал`}
              note="карт дээр дарж тэр төлөвийн гутлыг харна"
            >
              <StatusTiles counts={d.counts.byStatus} />
            </Section>
          </div>
        );
      }}
    </Load>
  );
}

/**
 * Нийлүүлэгчид төлөх өр — ₽. Өмнө нь Тойм табд байж, Нийлүүлэлт табын
 * «Өр» карттай ижил үлдэгдлийг давтдаг байв (2026-09-29 нэг газар).
 *
 * `/reports/dashboard`-аас: төлөгдөөгүй аялал, хамгийн эртний өр,
 * төлөгдсөн хувь гурав нь `/reports/suppliers`-т байхгүй.
 */
function SupplierCredit({ credit }: { credit: NonNullable<Dashboard['credit']> }) {
  return (
    <Section
      title="Нийлүүлэгчид төлөх"
      subtitle={`${formatQty(credit.suppliers)} нийлүүлэгч`}
      actions={
        <Link to="/credit" className="text-[12.5px] text-accent-700 hover:underline">Зээл, төлөлт →</Link>
      }
    >
      <StatGrid>
        <StatCard label="Үлдэгдэл" icon="coins" value={formatRub(credit.balanceRub)} />
        <StatCard
          label="Төлөгдөөгүй аялал"
          icon="truck"
          value={formatQty(credit.purchases)}
          delta={{ text: credit.oldestAt ? `хамгийн эрт ${formatDate(credit.oldestAt)}` : '—' }}
        />
        <StatCard
          label="Төлөгдсөн"
          icon="percent"
          value={`${credit.paidPct}%`}
          delta={{ text: 'нээлттэй зээлээс', good: credit.paidPct > 50 }}
          meter={credit.paidPct}
        />
      </StatGrid>
    </Section>
  );
}

// ------------------------------------------------------------ Нөөц ба байршил
/**
 * Нөөц — эхлээд хүрээ (Нийт / Салбар / Агуулах), дараа нь нэгж → байршил
 * (захиалагчийн цэгцлэл №4, №5, 2026-10-05).
 *
 * ⚠️ Хуучин «Нөөц» ба «Салбар» хоёр табыг НЭГТГЭВ: хоёулаа ижил нөөцийг
 *    өөр хүсэлтээс (`/reports/stock`, `/reports/branches`) харуулж,
 *    Тоймын байршлын хүснэгттэй гурвуулаа давхардаж байв. Одоо НЭГ
 *    хүсэлт (`/reports/stock-locations`) — нэгжийн тоо = байршлуудынх
 *    нь нийлбэр, сервер дээр.
 * ⚠️ Хасагдсан: «Шинээр орсон (7 хоног)» — Тоймын сарын графиктай
 *    давхардана; «Салбарт шилжсэн» — «Шилжүүлэг» хуудсанд; салбарын
 *    «Байршил», «Ажилтан» тоо — нөөцийн мэдээлэл биш.
 * ⚠️ `/branches` (Салбарын тойм) хуудас ХЭВЭЭР — салбарын менежер
 *    Статистик хардаггүй (`dashboard.view` алга), тэр нь түүний хуудас.
 */
const SCOPES: { key: StockScope; label: string }[] = [
  { key: 'all', label: 'Нийт' },
  { key: 'retail', label: 'Салбар' },
  { key: 'warehouse', label: 'Агуулах' },
];

const SCOPE_NOTE: Record<StockScope, string> = {
  all: 'салбар, агуулах, замд',
  retail: 'бүх салбарын байршилд',
  warehouse: 'бүх агуулахын байршилд',
};

function StockTab() {
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);
  const [scope, setScope] = useState<StockScope>('all');

  const q = useQuery({
    queryKey: ['stock-locations', scope],
    queryFn: () => api<Envelope<StockLocations>>(`/reports/stock-locations?scope=${scope}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Tabs tabs={SCOPES} value={scope} onChange={setScope} />
      </div>

      <Load q={q}>
        {(res) => {
          const s = res.data;
          const t = s.totals;
          return (
            <div className={`flex flex-col gap-3 transition ${q.isPlaceholderData ? 'opacity-60' : ''}`}>
              <StatGrid>
                <StatCard
                  label="Байгаа гутал"
                  icon="box"
                  value={formatQty(t.items)}
                  delta={{
                    text: s.scope === 'all' && s.outside > 0
                      ? `үүнээс ${formatQty(s.outside)} нь замд, Орост`
                      : SCOPE_NOTE[s.scope],
                  }}
                  info={
                    <Help
                      about="Байршилд биетээр байгаа гутал — гэмтэлтэй, тусгаарлалтад байгаа ч орно. Зарагдсан, актлагдсан орохгүй."
                      howTo="Доорх салбар, байршил дээр дарж яг аль гутал болохыг харна."
                    />
                  }
                />
                <StatCard
                  label="Зарагдахад бэлэн"
                  icon="store"
                  value={formatQty(t.sellable)}
                  delta={{ text: `байгаагийн ${t.items > 0 ? Math.round((t.sellable / t.items) * 100) : 0}%` }}
                  meter={t.items > 0 ? (t.sellable / t.items) * 100 : 0}
                  info={<Help about="Лангуу, үзмэрт байгаа бэлэн эсвэл буцаагдсан гутал — кассаар шууд зарж болно." />}
                />
                <StatCard
                  label="Шошгогүй"
                  icon="box"
                  value={formatQty(t.unlabeled)}
                  delta={{ text: 'баркод хүлээж буй', good: t.unlabeled === 0 }}
                  info={
                    <Help
                      about="Энэ хүрээнд байгаа, баркод (серийн дугаар) олгогдоогүй гутал."
                      howTo="«Бүх гутал» → «Шошгогүй» шүүлтүүрээр олоод агуулахад баркод олгоно."
                    />
                  }
                />
                <StatCard
                  label="Нөөцийн өртөг"
                  icon="coins"
                  value={showCost ? formatMntShort(t.landedCostMnt) : '•••'}
                  delta={{ text: showCost ? 'тээвэр, гааль оруулсан' : 'эрх алга' }}
                  pending={!showCost}
                  info={<Help about="Энэ хүрээнд байгаа гутлын авсан өртгийн нийлбэр. Өртөг хуваарилаагүй аяллын гутал 0-ээр орно." />}
                />
              </StatGrid>

              <Section
                title={s.scope === 'warehouse' ? 'Агуулах бүрээр' : s.scope === 'retail' ? 'Салбар бүрээр' : 'Салбар, агуулах бүрээр'}
                subtitle={`${formatQty(s.units.length)} нэгж`}
                note="нэгж эсвэл байршил дээр дарж гутлыг нь харна"
              >
                <LocationTree units={s.units} showCost={showCost} />
              </Section>

              <Section
                title="Размерын тархалт"
                subtitle={`${formatQty(s.sizes.length)} размер`}
                note="зарагдахуйц нөөц — аль размер дуусч байгааг эндээс харна"
              >
                <ColumnChart
                  ariaLabel="Размер бүрийн үлдэгдэл"
                  points={s.sizes.map((z) => ({
                    key: String(z.size),
                    label: formatSize(z.size),
                    title: `${formatSize(z.size)} размер`,
                    value: z.count,
                  }))}
                />
              </Section>
            </div>
          );
        }}
      </Load>
    </div>
  );
}

// ------------------------------------------------------------ Нийлүүлэлт
interface SupplierRow {
  id: string;
  code: string;
  name: string;
  region: string | null;
  items: number;
  purchases: number;
  lastCollectedAt: string | null;
  creditRub: string | null;
  creditTrips: number;
}

interface SupplyReport {
  counts: {
    total: number;
    active: number;
    itemsThisMonth: number;
    purchases: number;
    avgLandedMnt: string | null;
    creditRub: string | null;
    creditSuppliers: number;
  };
  suppliers: SupplierRow[];
}

function SupplyTab() {
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);

  const q = useQuery({
    queryKey: ['suppliers-report'],
    queryFn: () => api<Envelope<SupplyReport>>('/reports/suppliers'),
  });
  // Тойм табынхтай ижил түлхүүр — өрийн блок нэмэлт хүсэлтгүй. Өртөг
  // харахгүй хүнд `credit` нь null тул хүсэлт илгээхгүй.
  const dash = useQuery({
    queryKey: ['dashboard', 'month'],
    queryFn: () => api<Envelope<Dashboard>>('/reports/dashboard?period=month'),
    enabled: showCost,
  });
  const credit = dash.data?.data.credit;

  return (
    <Load q={q}>
      {(res) => {
        const { counts, suppliers } = res.data;
        const delivered = suppliers.filter((s) => s.items > 0).sort((a, b) => b.items - a.items);
        // Мөнгө string-ээр ирнэ — `Number()` зөвхөн туузны уртад
        const owed = suppliers
          .filter((s) => s.creditTrips > 0)
          .map((s) => ({ key: s.id, label: s.name, sub: s.code, value: Number(s.creditRub) }))
          .sort((a, b) => b.value - a.value);

        return (
          <div className="flex flex-col gap-3">
            {/*
              ⚠️ «N аялал» өмнө нь «Энэ сард ирсэн»-ий доор байж энэ сарынх мэт
                 уншигддаг байв — бүх хугацааных. Одоо өөрийн карттай.
              ⚠️ «Өр» картыг ХАСАВ: үлдэгдэл нь доорх «Нийлүүлэгчид төлөх»
                 блокт (Тоймоос шилжсэн) ижил тоогоор байна.
            */}
            <StatGrid>
              <StatCard
                label="Энэ сард ирсэн"
                icon="truck"
                value={`${formatQty(counts.itemsThisMonth)} ш`}
                delta={{ text: 'аяллаар ирсэн гутал', good: true }}
              />
              <StatCard
                label="Аялал"
                icon="pin"
                value={formatQty(counts.purchases)}
                delta={{ text: 'бүх хугацаанд' }}
              />
              <StatCard
                label="Нийлүүлсэн"
                icon="users"
                value={formatQty(delivered.length)}
                delta={{ text: `нийт ${formatQty(counts.total)} бүртгэлтэй` }}
              />
              <StatCard
                label="Дундаж авах өртөг"
                icon="coins"
                value={showCost ? formatMnt(counts.avgLandedMnt) : '•••'}
                delta={{ text: 'тээвэр, гааль оруулсан' }}
                pending={!showCost}
              />
            </StatGrid>

            {/* Өр нь зөвхөн энэ блокт — алдааг чимээгүй залгихгүй */}
            {dash.error ? (
              <ErrorNote>{(dash.error as Error).message}</ErrorNote>
            ) : credit ? (
              <SupplierCredit credit={credit} />
            ) : null}

            {/*
              ⚠️ «Нийлүүлэгч бүрээр» туузыг ХАСАВ — хүснэгтийн «Гутал» баганатай
                 ижил тоо, ижил эрэмбэ. Өрийн тууз үлдэв: өрөөр ЭРЭМБЭЛСЭН
                 цорын ганц жагсаалт; хүснэгтийн «Өр» баганыг оронд нь хасав.
            */}
            <div className={`grid items-start gap-3 ${showCost ? 'xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]' : ''}`}>
              <Section title="Нийлүүлэгч бүрээр" subtitle={`${formatQty(delivered.length)} нийлүүлэгч`}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Нийлүүлэгч</Th>
                      <Th>Бүс</Th>
                      <Th num>Аялал</Th>
                      <Th num>Гутал</Th>
                      <Th num>Сүүлд</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {delivered.map((s) => (
                      <tr key={s.id} className="border-t border-hair">
                        <Td>
                          <span className="flex flex-col">
                            <span className="text-pretty">{s.name}</span>
                            <span className="num text-label text-ink-3">{s.code}</span>
                          </span>
                        </Td>
                        <Td><span className="text-ink-2">{s.region ?? '—'}</span></Td>
                        <Td num>{formatQty(s.purchases)}</Td>
                        <Td num>{formatQty(s.items)}</Td>
                        <Td num>{s.lastCollectedAt ? formatDate(s.lastCollectedAt) : '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>

              {showCost ? (
                <Section title="Өр нийлүүлэгч бүрээр" subtitle={`${formatQty(owed.length)} нийлүүлэгч`}>
                  <RankBars rows={owed} tone="alert" format={formatRub} empty="Өр алга" />
                </Section>
              ) : null}
            </div>
          </div>
        );
      }}
    </Load>
  );
}
